import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  FiEdit2,
  FiImage,
  FiPlus,
  FiRefreshCw,
  FiSave,
  FiTrash2,
  FiUploadCloud,
  FiX,
} from "react-icons/fi";

import API from "../../../../../utils/apiCalling";

import {
  config,
} from "../../../../../utils/apiUrl";

import "./StorefrontManagement.css";
import "./StorefrontManagement.preview.css";


/*
|--------------------------------------------------------------------------
| DEFAULTS
|--------------------------------------------------------------------------
*/

const EMPTY_BRANDING = {
  logo:
    "",

  logoMainText:
    "",

  logoText:
    "",

  logoSubText:
    "",

  status:
    "active",
};


const EMPTY_BANNER = {
  _id:
    "",

  title:
    "",

  subtitle:
    "",

  image:
    "",

  mobileImage:
    "",

  link:
    "/products",

  buttonText:
    "Shop Now",

  sortOrder:
    0,

  status:
    "active",

  startDate:
    "",

  endDate:
    "",
};


/*
|--------------------------------------------------------------------------
| HELPERS
|--------------------------------------------------------------------------
*/

const toDateInput =
  (
    value
  ) => {

    if (
      !value
    ) {
      return "";
    }


    const date =
      new Date(
        value
      );


    if (
      Number.isNaN(
        date.getTime()
      )
    ) {
      return "";
    }


    return date
      .toISOString()
      .slice(
        0,
        10
      );

  };


const getAssetUrl =
  (
    value
  ) => {

    if (
      !value
    ) {
      return "";
    }


    if (
      /^https?:\/\//i.test(
        value
      )
    ) {
      return value;
    }


    const base =
      String(
        process.env
          .REACT_APP_API_URL ||
        ""
      )
        .replace(
          /\/+$/,
          ""
        );


    if (
      !base
    ) {
      return value;
    }


    return `${base}/${String(
      value
    ).replace(
      /^\/+/,
      ""
    )}`;

  };


const showMessage =
  (
    type,
    message
  ) => {

    if (
      typeof window !==
        "undefined" &&
      window.Swal
    ) {
      window.Swal.fire({
        icon:
          type,

        title:
          type ===
          "success"
            ?
            "Success"
            :
            "Unable to continue",

        text:
          message,

        confirmButtonText:
          "OK",
      });

      return;
    }


    window.alert(
      message
    );

  };


/*
|--------------------------------------------------------------------------
| COMPONENT
|--------------------------------------------------------------------------
*/

const StorefrontManagement =
  () => {

    const api =
      useMemo(
        () =>
          new API(),
        []
      );


    const [
      loading,
      setLoading,
    ] =
      useState(
        true
      );


    const [
      savingBranding,
      setSavingBranding,
    ] =
      useState(
        false
      );


    const [
      savingBanner,
      setSavingBanner,
    ] =
      useState(
        false
      );


    const [
      storefront,
      setStorefront,
    ] =
      useState(
        null
      );


    const [
      branding,
      setBranding,
    ] =
      useState(
        EMPTY_BRANDING
      );


    const [
      logoFile,
      setLogoFile,
    ] =
      useState(
        null
      );


    const [
      logoPreview,
      setLogoPreview,
    ] =
      useState(
        ""
      );


    const [
      brandingMode,
      setBrandingMode,
    ] =
      useState(
        "text"
      );


    const [
      removeLogoRequested,
      setRemoveLogoRequested,
    ] =
      useState(
        false
      );


    const [
      previewDevice,
      setPreviewDevice,
    ] =
      useState(
        "desktop"
      );


    const [
      previewBannerIndex,
      setPreviewBannerIndex,
    ] =
      useState(
        0
      );


    const [
      bannerModalOpen,
      setBannerModalOpen,
    ] =
      useState(
        false
      );


    const [
      bannerForm,
      setBannerForm,
    ] =
      useState(
        EMPTY_BANNER
      );


    const [
      bannerImageFile,
      setBannerImageFile,
    ] =
      useState(
        null
      );


    const [
      mobileBannerImageFile,
      setMobileBannerImageFile,
    ] =
      useState(
        null
      );


    const [
      bannerImagePreview,
      setBannerImagePreview,
    ] =
      useState(
        ""
      );


    const [
      mobileBannerImagePreview,
      setMobileBannerImagePreview,
    ] =
      useState(
        ""
      );


    /*
    |--------------------------------------------------------------------------
    | LOAD
    |--------------------------------------------------------------------------
    */

    const loadStorefront =
      useCallback(
        async () => {

          setLoading(
            true
          );


          try {
            const response =
              await api.get(
                config
                  .getStorefront
              );


            if (
              response
                ?.success ===
              false
            ) {
              throw new Error(
                response
                  ?.message ||
                "Unable to load storefront."
              );
            }


            const data =
              response?.data ||
              {};


            setStorefront(
              data
            );


            setBranding({
              logo:
                data.logo ||
                "",

              logoMainText:
                data.logoMainText ||
                "",

              logoText:
                data.logoText ||
                "",

              logoSubText:
                data.logoSubText ||
                "",

              status:
                data.status ||
                "active",
            });


            setLogoFile(
              null
            );


            setLogoPreview(
              getAssetUrl(
                data.logo
              )
            );


            setBrandingMode(
              data.logo
                ?
                "logo"
                :
                "text"
            );

            setRemoveLogoRequested(
              false
            );

          } catch (
            error
          ) {
            showMessage(
              "error",
              error?.message ||
              "Unable to load storefront."
            );

          } finally {
            setLoading(
              false
            );
          }

        },
        [
          api,
        ]
      );


    useEffect(
      () => {

        loadStorefront();

      },
      [
        loadStorefront,
      ]
    );


    /*
    |--------------------------------------------------------------------------
    | OBJECT URL CLEANUP
    |--------------------------------------------------------------------------
    */

    useEffect(
      () => {

        return () => {
          [
            logoPreview,
            bannerImagePreview,
            mobileBannerImagePreview,
          ]
            .forEach(
              (
                value
              ) => {

                if (
                  value?.startsWith(
                    "blob:"
                  )
                ) {
                  URL.revokeObjectURL(
                    value
                  );
                }

              }
            );
        };

      },
      [
        logoPreview,
        bannerImagePreview,
        mobileBannerImagePreview,
      ]
    );


    /*
    |--------------------------------------------------------------------------
    | BRANDING
    |--------------------------------------------------------------------------
    */

    const handleBrandingChange =
      (
        event
      ) => {

        const {
          name,
          value,
        } =
          event.target;


        setBranding(
          (
            previous
          ) => ({
            ...previous,

            [name]:
              value,
          })
        );

      };


    const handleLogoChange =
      (
        event
      ) => {

        const file =
          event.target
            .files?.[0] ||
          null;


        setLogoFile(
          file
        );


        if (
          file
        ) {
          setLogoPreview(
            URL.createObjectURL(
              file
            )
          );


          setBrandingMode(
            "logo"
          );

          setRemoveLogoRequested(
            false
          );
        }

      };


    const saveBranding =
      async () => {

        setSavingBranding(
          true
        );


        try {
          const formData =
            new FormData();


          formData.append(
            "logoMainText",
            branding.logoMainText
          );


          formData.append(
            "logoText",
            branding.logoText
          );


          formData.append(
            "logoSubText",
            branding.logoSubText
          );


          formData.append(
            "status",
            branding.status
          );


          if (
            logoFile
          ) {
            formData.append(
              "logo",
              logoFile
            );
          }


          if (
            removeLogoRequested
          ) {
            formData.append(
              "removeLogo",
              "true"
            );
          }


          const response =
            await api.putFormData(
              config
                .updateStorefrontBranding,
              formData
            );


          if (
            response
              ?.success ===
            false
          ) {
            throw new Error(
              response
                ?.message ||
              "Unable to save branding."
            );
          }


          showMessage(
            "success",
            "Storefront branding updated successfully."
          );


          await loadStorefront();

        } catch (
          error
        ) {
          showMessage(
            "error",
            error?.message ||
            "Unable to save branding."
          );

        } finally {
          setSavingBranding(
            false
          );
        }

      };


    /*
    |--------------------------------------------------------------------------
    | BANNER MODAL
    |--------------------------------------------------------------------------
    */

    const closeBannerModal =
      () => {

        setBannerModalOpen(
          false
        );


        setBannerForm(
          EMPTY_BANNER
        );


        setBannerImageFile(
          null
        );


        setMobileBannerImageFile(
          null
        );


        setBannerImagePreview(
          ""
        );


        setMobileBannerImagePreview(
          ""
        );

      };


    const openAddBanner =
      () => {

        setBannerForm(
          EMPTY_BANNER
        );


        setBannerImageFile(
          null
        );


        setMobileBannerImageFile(
          null
        );


        setBannerImagePreview(
          ""
        );


        setMobileBannerImagePreview(
          ""
        );


        setBannerModalOpen(
          true
        );

      };


    const openEditBanner =
      (
        banner
      ) => {

        setBannerForm({
          _id:
            banner._id,

          title:
            banner.title ||
            "",

          subtitle:
            banner.subtitle ||
            "",

          image:
            banner.image ||
            "",

          mobileImage:
            banner.mobileImage ||
            "",

          link:
            banner.link ||
            "/products",

          buttonText:
            banner.buttonText ||
            "Shop Now",

          sortOrder:
            Number(
              banner.sortOrder
            ) ||
            0,

          status:
            banner.status ||
            "active",

          startDate:
            toDateInput(
              banner.startDate
            ),

          endDate:
            toDateInput(
              banner.endDate
            ),
        });


        setBannerImageFile(
          null
        );


        setMobileBannerImageFile(
          null
        );


        setBannerImagePreview(
          getAssetUrl(
            banner.image
          )
        );


        setMobileBannerImagePreview(
          getAssetUrl(
            banner.mobileImage
          )
        );


        setBannerModalOpen(
          true
        );

      };


    const handleBannerChange =
      (
        event
      ) => {

        const {
          name,
          value,
        } =
          event.target;


        setBannerForm(
          (
            previous
          ) => ({
            ...previous,

            [name]:
              name ===
              "sortOrder"
                ?
                Number(
                  value
                ) ||
                0
                :
                value,
          })
        );

      };


    const handleBannerImage =
      (
        event,
        mobile =
          false
      ) => {

        const file =
          event.target
            .files?.[0] ||
          null;


        if (
          mobile
        ) {
          setMobileBannerImageFile(
            file
          );


          if (
            file
          ) {
            setMobileBannerImagePreview(
              URL.createObjectURL(
                file
              )
            );
          }

          return;
        }


        setBannerImageFile(
          file
        );


        if (
          file
        ) {
          setBannerImagePreview(
            URL.createObjectURL(
              file
            )
          );
        }

      };


    const saveBanner =
      async () => {

        if (
          !bannerForm._id &&
          !bannerImageFile
        ) {
          showMessage(
            "error",
            "Banner image is required."
          );

          return;
        }


        setSavingBanner(
          true
        );


        try {
          const formData =
            new FormData();


          [
            "title",
            "subtitle",
            "link",
            "buttonText",
            "sortOrder",
            "status",
            "startDate",
            "endDate",
          ]
            .forEach(
              (
                field
              ) => {

                formData.append(
                  field,
                  String(
                    bannerForm[
                      field
                    ] ??
                    ""
                  )
                );

              }
            );


          if (
            bannerImageFile
          ) {
            formData.append(
              "image",
              bannerImageFile
            );
          }


          if (
            mobileBannerImageFile
          ) {
            formData.append(
              "mobileImage",
              mobileBannerImageFile
            );
          }


          const response =
            bannerForm._id
              ?
              await api.putFormData(
                config
                  .updateStorefrontBanner(
                    bannerForm._id
                  ),
                formData
              )
              :
              await api.postFormData(
                config
                  .createStorefrontBanner,
                formData
              );


          if (
            response
              ?.success ===
            false
          ) {
            throw new Error(
              response
                ?.message ||
              "Unable to save banner."
            );
          }


          showMessage(
            "success",
            bannerForm._id
              ?
              "Banner updated successfully."
              :
              "Banner added successfully."
          );


          closeBannerModal();


          await loadStorefront();

        } catch (
          error
        ) {
          showMessage(
            "error",
            error?.message ||
            "Unable to save banner."
          );

        } finally {
          setSavingBanner(
            false
          );
        }

      };


    const toggleBannerStatus =
      async (
        banner
      ) => {

        try {
          const nextStatus =
            banner.status ===
              "active"
              ?
              "inactive"
              :
              "active";


          const response =
            await api.patch(
              config
                .changeStorefrontBannerStatus(
                  banner._id
                ),
              {
                status:
                  nextStatus,
              }
            );


          if (
            response
              ?.success ===
            false
          ) {
            throw new Error(
              response
                ?.message ||
              "Unable to update banner status."
            );
          }


          await loadStorefront();

        } catch (
          error
        ) {
          showMessage(
            "error",
            error?.message ||
            "Unable to update banner status."
          );
        }

      };


    const deleteBanner =
      async (
        banner
      ) => {

        const confirmed =
          window.confirm(
            `Delete banner "${banner.title || "Banner"}"?`
          );


        if (
          !confirmed
        ) {
          return;
        }


        try {
          const response =
            await api.delete(
              config
                .deleteStorefrontBanner(
                  banner._id
                )
            );


          if (
            response
              ?.success ===
            false
          ) {
            throw new Error(
              response
                ?.message ||
              "Unable to delete banner."
            );
          }


          showMessage(
            "success",
            "Banner deleted successfully."
          );


          await loadStorefront();

        } catch (
          error
        ) {
          showMessage(
            "error",
            error?.message ||
            "Unable to delete banner."
          );
        }

      };


    const banners =
      useMemo(
        () =>
          [
            ...(
              storefront
                ?.banners ||
              []
            ),
          ]
            .sort(
              (
                a,
                b
              ) =>
                Number(
                  a.sortOrder ||
                  0
                ) -
                Number(
                  b.sortOrder ||
                  0
                )
            ),
        [
          storefront,
        ]
      );


    const clearLogoImage =
      () => {
        setLogoFile(
          null
        );

        setLogoPreview(
          ""
        );

        setRemoveLogoRequested(
          true
        );

        setBrandingMode(
          "text"
        );
      };


    const clearTextLogo =
      () => {
        setBranding(
          (
            current
          ) => ({
            ...current,
            logoMainText:
              "",
            logoText:
              "",
            logoSubText:
              "",
          })
        );
      };


    const previewBanners =
      useMemo(
        () => {
          const active =
            banners.filter(
              (
                banner
              ) =>
                banner.status ===
                "active"
            );

          if (
            bannerModalOpen &&
            (
              bannerImagePreview ||
              bannerForm.image
            )
          ) {
            const draft = {
              ...bannerForm,
              _id:
                bannerForm._id ||
                "__draft__",
              image:
                bannerImagePreview ||
                getAssetUrl(
                  bannerForm.image
                ),
              mobileImage:
                mobileBannerImagePreview ||
                getAssetUrl(
                  bannerForm.mobileImage
                ),
            };

            const withoutEdited =
              active.filter(
                (
                  banner
                ) =>
                  banner._id !==
                  bannerForm._id
              );

            return [
              draft,
              ...withoutEdited,
            ];
          }

          return active;
        },
        [
          banners,
          bannerModalOpen,
          bannerForm,
          bannerImagePreview,
          mobileBannerImagePreview,
        ]
      );


    useEffect(
      () => {
        if (
          previewBannerIndex >=
          previewBanners.length
        ) {
          setPreviewBannerIndex(
            0
          );
        }
      },
      [
        previewBannerIndex,
        previewBanners.length,
      ]
    );


    const activePreviewBanner =
      previewBanners[
        previewBannerIndex
      ] ||
      null;


    /*
    |--------------------------------------------------------------------------
    | LOADING
    |--------------------------------------------------------------------------
    */

    if (
      loading
    ) {
      return (
        <div className="storefront-page-state">

          <FiRefreshCw className="spin" />

          <strong>
            Loading Storefront
          </strong>

          <span>
            Fetching your branding and banners...
          </span>

        </div>
      );
    }


    return (
      <div className="storefront-management-page">

        <div className="storefront-page-header">

          <div>

            <span className="storefront-eyebrow">
              ONLINE STORE
            </span>

            <h1>
              Storefront Management
            </h1>

            <p>
              Manage the logo, brand text and homepage carousel banners for this tenant.
            </p>

          </div>


          <button
            type="button"
            className="storefront-secondary-button"
            onClick={
              loadStorefront
            }
          >

            <FiRefreshCw />

            Refresh

          </button>

        </div>


        <section className="storefront-builder-preview-card">

          <div className="storefront-builder-preview-toolbar">

            <div>
              <span className="storefront-eyebrow">
                LIVE PREVIEW
              </span>

              <h2>
                Customer Storefront
              </h2>

              <p>
                Changes below are previewed instantly before you save.
              </p>
            </div>

            <div className="storefront-preview-device-switch">
              <button
                type="button"
                className={
                  previewDevice ===
                  "desktop"
                    ?
                    "active"
                    :
                    ""
                }
                onClick={() =>
                  setPreviewDevice(
                    "desktop"
                  )
                }
              >
                Desktop
              </button>

              <button
                type="button"
                className={
                  previewDevice ===
                  "mobile"
                    ?
                    "active"
                    :
                    ""
                }
                onClick={() =>
                  setPreviewDevice(
                    "mobile"
                  )
                }
              >
                Mobile
              </button>
            </div>

          </div>

          <div
            className={`storefront-live-frame ${previewDevice}`}
          >
            <div className="storefront-live-header">

              <div className="storefront-live-brand">
                {
                  brandingMode ===
                    "logo" &&
                  logoPreview
                    ?
                    (
                      <img
                        src={logoPreview}
                        alt="Store logo"
                      />
                    )
                    :
                    (
                      <>
                        <span className="storefront-live-monogram">
                          {
                            (
                              branding.logoMainText ||
                              "CK"
                            )
                              .trim()
                              .slice(
                                0,
                                2
                              )
                              .toUpperCase()
                          }
                        </span>

                        <span className="storefront-live-brand-copy">
                          <strong>
                            {
                              branding.logoText ||
                              branding.logoMainText ||
                              "ComKwik"
                            }
                          </strong>

                          <small>
                            {
                              branding.logoSubText ||
                              "Computers"
                            }
                          </small>
                        </span>
                      </>
                    )
                }
              </div>

              <div className="storefront-live-search">
                Search laptops, computers, accessories...
              </div>

              <div className="storefront-live-cart">
                Cart
              </div>

            </div>

            <div className="storefront-live-nav">
              <strong>☰ All Products</strong>
              <span>Laptop</span>
              <span>Featured</span>
              <span>New Arrivals</span>
            </div>

            <div className="storefront-live-benefits">
              <span>✓ Secure Payments</span>
              <span>✓ Quality Checked Products</span>
              <span>✓ Fast Delivery</span>
            </div>

            <div className="storefront-live-banner">
              {
                activePreviewBanner
                  ?
                  (
                    <>
                      <img
                        src={
                          previewDevice ===
                            "mobile" &&
                          activePreviewBanner
                            .mobileImage
                            ?
                            getAssetUrl(
                              activePreviewBanner
                                .mobileImage
                            )
                            :
                            getAssetUrl(
                              activePreviewBanner
                                .image
                            )
                        }
                        alt={
                          activePreviewBanner
                            .title ||
                          "Banner preview"
                        }
                      />

                      {
                        previewBanners.length >
                          1 &&
                        (
                          <div className="storefront-live-dots">
                            {
                              previewBanners.map(
                                (
                                  banner,
                                  index
                                ) => (
                                  <button
                                    type="button"
                                    key={
                                      banner._id ||
                                      index
                                    }
                                    className={
                                      index ===
                                        previewBannerIndex
                                        ?
                                        "active"
                                        :
                                        ""
                                    }
                                    onClick={() =>
                                      setPreviewBannerIndex(
                                        index
                                      )
                                    }
                                    aria-label={`Preview banner ${index + 1}`}
                                  />
                                )
                              )
                            }
                          </div>
                        )
                      }
                    </>
                  )
                  :
                  (
                    <div className="storefront-live-banner-empty">
                      <FiImage />
                      <strong>
                        Banner preview
                      </strong>
                      <span>
                        Add a banner to see it here.
                      </span>
                    </div>
                  )
              }
            </div>
          </div>

        </section>


        <section className="storefront-card">

          <div className="storefront-card-header">

            <div>

              <span className="storefront-card-icon">
                <FiImage />
              </span>

              <div>

                <h2>
                  Store Branding
                </h2>

                <p>
                  This content is displayed on the customer storefront.
                </p>

              </div>

            </div>

          </div>


          <div className="storefront-brand-mode">

            <div>
              <strong>
                Brand Identity
              </strong>
              <span>
                Choose what customers see in the storefront header.
              </span>
            </div>

            <div className="storefront-brand-mode-options">

              <button
                type="button"
                className={
                  brandingMode ===
                    "logo"
                    ?
                    "active"
                    :
                    ""
                }
                onClick={() =>
                  setBrandingMode(
                    "logo"
                  )
                }
              >
                <FiImage />
                Use Logo Image
              </button>

              <button
                type="button"
                className={
                  brandingMode ===
                    "text"
                    ?
                    "active"
                    :
                    ""
                }
                onClick={() =>
                  setBrandingMode(
                    "text"
                  )
                }
              >
                Aa
                Use Text Logo
              </button>

            </div>

          </div>


          <div
            className={`storefront-branding-grid branding-mode-${brandingMode}`}
          >

            <div className="storefront-logo-column">

              <label>
                Store Logo
              </label>


              <div className="storefront-logo-preview">

                {
                  logoPreview
                    ?
                    (
                      <img
                        src={
                          logoPreview
                        }
                        alt="Store logo preview"
                      />
                    )
                    :
                    (
                      <div className="storefront-empty-logo">
                        <FiImage />
                        <span>
                          No logo
                        </span>
                      </div>
                    )
                }

              </div>


              <div className="storefront-logo-actions">

                <label className="storefront-upload-button">

                  <FiUploadCloud />

                  Choose Logo

                  <input
                    type="file"
                    accept="image/*"
                    onChange={
                      handleLogoChange
                    }
                    disabled={
                      brandingMode !==
                      "logo"
                    }
                  />

                </label>


                <button
                  type="button"
                  className="storefront-brand-delete-button"
                  onClick={
                    clearLogoImage
                  }
                  disabled={
                    !logoPreview &&
                    !logoFile
                  }
                  title="Delete store logo"
                >
                  <FiTrash2 />

                  Delete Logo
                </button>

              </div>

            </div>


            <div className="storefront-form-grid">

              <label>

                <span>
                  Logo Main Text
                </span>

                <input
                  name="logoMainText"
                  disabled={
                    brandingMode !==
                    "text"
                  }
                  value={
                    branding.logoMainText
                  }
                  onChange={
                    handleBrandingChange
                  }
                  placeholder="ComKwik"
                />

              </label>


              <label>

                <span>
                  Logo Text
                </span>

                <input
                  name="logoText"
                  disabled={
                    brandingMode !==
                    "text"
                  }
                  value={
                    branding.logoText
                  }
                  onChange={
                    handleBrandingChange
                  }
                  placeholder="Computers"
                />

              </label>


              <label className="storefront-field-wide">

                <span>
                  Logo Sub Text
                </span>

                <input
                  name="logoSubText"
                  disabled={
                    brandingMode !==
                    "text"
                  }
                  value={
                    branding.logoSubText
                  }
                  onChange={
                    handleBrandingChange
                  }
                  placeholder="Your Digital Life, Simplified."
                />

              </label>


              <label>

                <span>
                  Storefront Status
                </span>

                <select
                  name="status"
                  value={
                    branding.status
                  }
                  onChange={
                    handleBrandingChange
                  }
                >
                  <option value="active">
                    Active
                  </option>

                  <option value="inactive">
                    Inactive
                  </option>
                </select>

              </label>


              <div className="storefront-text-logo-actions">

                <button
                  type="button"
                  className="storefront-brand-delete-button"
                  onClick={
                    clearTextLogo
                  }
                  disabled={
                    !branding.logoMainText &&
                    !branding.logoText &&
                    !branding.logoSubText
                  }
                  title="Delete logo text"
                >
                  <FiTrash2 />

                  Delete Logo Text
                </button>

              </div>

            </div>

          </div>


          <div className="storefront-card-footer">

            <button
              type="button"
              className="storefront-primary-button"
              onClick={
                saveBranding
              }
              disabled={
                savingBranding
              }
            >

              <FiSave />

              {
                savingBranding
                  ?
                  "Saving..."
                  :
                  "Save Branding"
              }

            </button>

          </div>

        </section>


        <section className="storefront-card">

          <div className="storefront-card-header storefront-card-header--actions">

            <div>

              <span className="storefront-card-icon">
                <FiImage />
              </span>

              <div>

                <h2>
                  Homepage Banners
                </h2>

                <p>
                  Add as many banners as required. Active banners are displayed as a carousel.
                </p>

              </div>

            </div>


            <button
              type="button"
              className="storefront-primary-button"
              onClick={
                openAddBanner
              }
            >

              <FiPlus />

              Add Banner

            </button>

          </div>


          {
            banners.length ===
            0
              ?
              (
                <div className="storefront-empty-state">

                  <FiImage />

                  <strong>
                    No banners added
                  </strong>

                  <span>
                    Add the first banner to start the homepage carousel.
                  </span>

                  <button
                    type="button"
                    onClick={
                      openAddBanner
                    }
                  >
                    <FiPlus />
                    Add Banner
                  </button>

                </div>
              )
              :
              (
                <div className="storefront-banner-grid">

                  {
                    banners.map(
                      (
                        banner
                      ) => (

                        <article
                          key={
                            banner._id
                          }
                          className="storefront-banner-card"
                        >

                          <div className="storefront-banner-image">

                            <img
                              src={
                                getAssetUrl(
                                  banner.image
                                )
                              }
                              alt={
                                banner.title ||
                                "Store banner"
                              }
                            />


                            <span
                              className={
                                banner.status ===
                                  "active"
                                  ?
                                  "storefront-status active"
                                  :
                                  "storefront-status inactive"
                              }
                            >
                              {
                                banner.status
                              }
                            </span>

                          </div>


                          <div className="storefront-banner-content">

                            <div className="storefront-banner-title-row">

                              <div>

                                <strong>
                                  {
                                    banner.title ||
                                    "Untitled Banner"
                                  }
                                </strong>

                                <span>
                                  Order {
                                    Number(
                                      banner.sortOrder
                                    ) ||
                                    0
                                  }
                                </span>

                              </div>

                            </div>


                            <p>
                              {
                                banner.subtitle ||
                                "No subtitle"
                              }
                            </p>


                            <div className="storefront-banner-meta">

                              <span>
                                {
                                  banner.buttonText ||
                                  "Shop Now"
                                }
                              </span>

                              <span>
                                {
                                  banner.link ||
                                  "/products"
                                }
                              </span>

                            </div>


                            <div className="storefront-banner-actions">

                              <button
                                type="button"
                                onClick={
                                  () =>
                                    openEditBanner(
                                      banner
                                    )
                                }
                              >
                                <FiEdit2 />
                                Edit
                              </button>


                              <button
                                type="button"
                                onClick={
                                  () =>
                                    toggleBannerStatus(
                                      banner
                                    )
                                }
                              >
                                {
                                  banner.status ===
                                    "active"
                                    ?
                                    "Disable"
                                    :
                                    "Enable"
                                }
                              </button>


                              <button
                                type="button"
                                className="danger"
                                onClick={
                                  () =>
                                    deleteBanner(
                                      banner
                                    )
                                }
                              >
                                <FiTrash2 />
                                Delete
                              </button>

                            </div>

                          </div>

                        </article>

                      )
                    )
                  }

                </div>
              )
          }

        </section>


        {
          bannerModalOpen
            ?
            (
              <div className="storefront-modal-backdrop">

                <div className="storefront-modal">

                  <div className="storefront-modal-header">

                    <div>

                      <span>
                        {
                          bannerForm._id
                            ?
                            "EDIT BANNER"
                            :
                            "NEW BANNER"
                        }
                      </span>

                      <h2>
                        {
                          bannerForm._id
                            ?
                            "Update Homepage Banner"
                            :
                            "Add Homepage Banner"
                        }
                      </h2>

                    </div>


                    <button
                      type="button"
                      className="storefront-modal-close"
                      onClick={
                        closeBannerModal
                      }
                    >
                      <FiX />
                    </button>

                  </div>


                  <div className="storefront-modal-body">

                    <div className="storefront-banner-upload-grid">

                      <label className="storefront-image-upload-box">

                        <span>
                          Desktop Banner *
                        </span>


                        {
                          bannerImagePreview
                            ?
                            (
                              <img
                                src={
                                  bannerImagePreview
                                }
                                alt="Desktop banner preview"
                              />
                            )
                            :
                            (
                              <div>
                                <FiUploadCloud />
                                <strong>
                                  Select desktop image
                                </strong>
                              </div>
                            )
                        }


                        <input
                          type="file"
                          accept="image/*"
                          onChange={
                            (
                              event
                            ) =>
                              handleBannerImage(
                                event,
                                false
                              )
                          }
                        />

                      </label>


                      <label className="storefront-image-upload-box">

                        <span>
                          Mobile Banner
                        </span>


                        {
                          mobileBannerImagePreview
                            ?
                            (
                              <img
                                src={
                                  mobileBannerImagePreview
                                }
                                alt="Mobile banner preview"
                              />
                            )
                            :
                            (
                              <div>
                                <FiUploadCloud />
                                <strong>
                                  Optional mobile image
                                </strong>
                              </div>
                            )
                        }


                        <input
                          type="file"
                          accept="image/*"
                          onChange={
                            (
                              event
                            ) =>
                              handleBannerImage(
                                event,
                                true
                              )
                          }
                        />

                      </label>

                    </div>


                    <div className="storefront-form-grid">

                      <label>

                        <span>
                          Title
                        </span>

                        <input
                          name="title"
                          value={
                            bannerForm.title
                          }
                          onChange={
                            handleBannerChange
                          }
                          placeholder="Laptop Mega Sale"
                        />

                      </label>


                      <label>

                        <span>
                          Subtitle
                        </span>

                        <input
                          name="subtitle"
                          value={
                            bannerForm.subtitle
                          }
                          onChange={
                            handleBannerChange
                          }
                          placeholder="Save more on selected products"
                        />

                      </label>


                      <label>

                        <span>
                          Button Text
                        </span>

                        <input
                          name="buttonText"
                          value={
                            bannerForm.buttonText
                          }
                          onChange={
                            handleBannerChange
                          }
                        />

                      </label>


                      <label>

                        <span>
                          Link
                        </span>

                        <input
                          name="link"
                          value={
                            bannerForm.link
                          }
                          onChange={
                            handleBannerChange
                          }
                          placeholder="/products"
                        />

                      </label>


                      <label>

                        <span>
                          Sort Order
                        </span>

                        <input
                          type="number"
                          min="0"
                          name="sortOrder"
                          value={
                            bannerForm.sortOrder
                          }
                          onChange={
                            handleBannerChange
                          }
                        />

                      </label>


                      <label>

                        <span>
                          Status
                        </span>

                        <select
                          name="status"
                          value={
                            bannerForm.status
                          }
                          onChange={
                            handleBannerChange
                          }
                        >
                          <option value="active">
                            Active
                          </option>

                          <option value="inactive">
                            Inactive
                          </option>
                        </select>

                      </label>


                      <label>

                        <span>
                          Start Date
                        </span>

                        <input
                          type="date"
                          name="startDate"
                          value={
                            bannerForm.startDate
                          }
                          onChange={
                            handleBannerChange
                          }
                        />

                      </label>


                      <label>

                        <span>
                          End Date
                        </span>

                        <input
                          type="date"
                          name="endDate"
                          value={
                            bannerForm.endDate
                          }
                          onChange={
                            handleBannerChange
                          }
                        />

                      </label>

                    </div>

                  </div>


                  <div className="storefront-modal-footer">

                    <button
                      type="button"
                      className="storefront-secondary-button"
                      onClick={
                        closeBannerModal
                      }
                    >
                      Cancel
                    </button>


                    <button
                      type="button"
                      className="storefront-primary-button"
                      onClick={
                        saveBanner
                      }
                      disabled={
                        savingBanner
                      }
                    >
                      <FiSave />

                      {
                        savingBanner
                          ?
                          "Saving..."
                          :
                          (
                            bannerForm._id
                              ?
                              "Update Banner"
                              :
                              "Add Banner"
                          )
                      }

                    </button>

                  </div>

                </div>

              </div>
            )
            :
            null
        }

      </div>
    );

  };


export default
  StorefrontManagement;
